import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from '@/Layout'
import ProtectedRoute from '@/Components/auth/ProtectedRoute'
import ScreenGate from '@/Components/auth/ScreenGate'
import PWAUpdatePrompt from '@/Components/pwa/PWAUpdatePrompt'

// Pages
import Home from '@/Pages/Home'
import Login from '@/Pages/Login'
import AgentTasks from '@/Pages/AgentTasks'
import AgentLeads from '@/Pages/AgentLeads'
import AgentOrders from '@/Pages/AgentOrders'
import AgentCatalog from '@/Pages/AgentCatalog'
import AgentProfile from '@/Pages/AgentProfile'
import AgentReport from '@/Pages/AgentReport'
import AdminDashboard from '@/Pages/AdminDashboard'
import AdminLeads from '@/Pages/AdminLeads'
import AdminTasks from '@/Pages/AdminTasks'
import AdminOrders from '@/Pages/AdminOrders'
import AdminAgents from '@/Pages/AdminAgents'
import AdminReports from '@/Pages/AdminReports'
import AdminInbox from '@/Pages/AdminInbox'
import AdminLeadHunter from '@/Pages/AdminLeadHunter'
import AdminAgentPerformance from '@/Pages/AdminAgentPerformance'
import AdminProducts from '@/Pages/AdminProducts'
import AdminStockMovements from '@/Pages/AdminStockMovements'
import AdminSuppliers from '@/Pages/AdminSuppliers'
import AdminPdfSettings from '@/Pages/AdminPdfSettings'
import AdminEwkKategorien from '@/Pages/AdminEwkKategorien'
import AdminShopifyOrders from '@/Pages/AdminShopifyOrders'
import WarehouseDashboard from '@/Pages/WarehouseDashboard'
import WarehouseWarenausgang from '@/Pages/WarehouseWarenausgang'
import WarehouseWareneingang from '@/Pages/WarehouseWareneingang'
import WarehouseDispo from '@/Pages/WarehouseDispo'
import WarehouseInventur from '@/Pages/WarehouseInventur'
import WarehouseSuppliers from '@/Pages/WarehouseSuppliers'
import ShippingDashboard from '@/Pages/ShippingDashboard'
import BuchhaltungDashboard from '@/Pages/BuchhaltungDashboard'
import BuchhaltungRechnungen from '@/Pages/BuchhaltungRechnungen'
import BuchhaltungMahnungen from '@/Pages/BuchhaltungMahnungen'
import BuchhaltungGutschriften from '@/Pages/BuchhaltungGutschriften'
import BuchhaltungJournal from '@/Pages/BuchhaltungJournal'
import BuchhaltungKonten from '@/Pages/BuchhaltungKonten'
import BuchhaltungBerichte from '@/Pages/BuchhaltungBerichte'
import BuchhaltungBank from '@/Pages/BuchhaltungBank'
import BuchhaltungAnlagen from '@/Pages/BuchhaltungAnlagen'
// Ausgaben-Modul lazy: Belegerfassung/OCR nur für Buchhaltung laden
const BuchhaltungAusgaben = lazy(() => import('@/Pages/BuchhaltungAusgaben'))
const BuchhaltungWiederkehrend = lazy(() => import('@/Pages/BuchhaltungWiederkehrend'))
const BuchhaltungAusgabeEditor = lazy(() => import('@/Pages/BuchhaltungAusgabeEditor'))
const BuchhaltungWiederkehrendEditor = lazy(() => import('@/Pages/BuchhaltungWiederkehrendEditor'))
const BuchhaltungRechnungEditor = lazy(() => import('@/Pages/BuchhaltungRechnungEditor'))
const BuchhaltungBankVerbinden = lazy(() => import('@/Pages/BuchhaltungBankVerbinden'))
const lazyPage = (el) => <Suspense fallback={<div className="p-10 text-center text-sm text-slate-400">Lade…</div>}>{el}</Suspense>
import BuchhaltungAbschluss from '@/Pages/BuchhaltungAbschluss'
// Kundenportal + Admin-Zentrale lazy: eigener Bundle-Teil
const AdminKundenportal = lazy(() => import('@/Pages/AdminKundenportal'))
const AdminIntegrationen = lazy(() => import('@/Pages/AdminIntegrationen'))
const AdminEinstellungen = lazy(() => import('@/Pages/AdminEinstellungen'))
const KundeLayout = lazy(() => import('@/Components/kunde/KundeLayout'))
const KundeLogin = lazy(() => import('@/Pages/kunde/KundeLogin'))
const KundePasswort = lazy(() => import('@/Pages/kunde/KundePasswort'))
const KundeUebersicht = lazy(() => import('@/Pages/kunde/KundeUebersicht'))
const KundeBestellungen = lazy(() => import('@/Pages/kunde/KundeBestellungen'))
const KundeBestellung = lazy(() => import('@/Pages/kunde/KundeBestellung'))
const KundeBestellen = lazy(() => import('@/Pages/kunde/KundeBestellen'))
const KundeWarenkorb = lazy(() => import('@/Pages/kunde/KundeWarenkorb'))
const KundeRechnungen = lazy(() => import('@/Pages/kunde/KundeRechnungen'))
const KundeZahlung = lazy(() => import('@/Pages/kunde/KundeZahlung'))
const KundeKonto = lazy(() => import('@/Pages/kunde/KundeKonto'))

function App() {
  return (
    <>
      <PWAUpdatePrompt />
      <Routes>
      {/* Home - Role Selection */}
      <Route path="/" element={<Home />} />

      {/* Login */}
      <Route path="/login" element={<Login />} />

      {/* Agent Routes - Protected */}
      <Route path="/agent" element={
        <ProtectedRoute requiredRole="agent">
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="tasks" replace />} />
        <Route path="tasks" element={<ScreenGate screen="AgentTasks"><AgentTasks /></ScreenGate>} />
        <Route path="leads" element={<ScreenGate screen="AgentLeads"><AgentLeads /></ScreenGate>} />
        <Route path="orders" element={<ScreenGate screen="AgentOrders"><AgentOrders /></ScreenGate>} />
        <Route path="catalog" element={<ScreenGate screen="AgentCatalog"><AgentCatalog /></ScreenGate>} />
        <Route path="profile" element={<ScreenGate screen="AgentProfile"><AgentProfile /></ScreenGate>} />
        <Route path="report" element={<ScreenGate screen="AgentReport"><AgentReport /></ScreenGate>} />
      </Route>

      {/* Warehouse Routes - Protected */}
      <Route path="/warehouse" element={
        <ProtectedRoute requiredRole="warehouse">
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="warenausgang" replace />} />
        <Route path="warenausgang" element={<ScreenGate screen="WarehouseWarenausgang"><WarehouseWarenausgang /></ScreenGate>} />
        <Route path="wareneingang" element={<ScreenGate screen="WarehouseWareneingang"><WarehouseWareneingang /></ScreenGate>} />
        <Route path="dispo" element={<ScreenGate screen="WarehouseDispo"><WarehouseDispo /></ScreenGate>} />
        <Route path="depo" element={<ScreenGate screen="WarehouseDashboard"><WarehouseDashboard /></ScreenGate>} />
        <Route path="inventur" element={<ScreenGate screen="WarehouseInventur"><WarehouseInventur /></ScreenGate>} />
        <Route path="suppliers" element={<ScreenGate screen="WarehouseSuppliers"><WarehouseSuppliers /></ScreenGate>} />
        <Route path="profile" element={<ScreenGate screen="AgentProfile"><AgentProfile /></ScreenGate>} />
      </Route>

      {/* Shipping Routes - Protected */}
      <Route path="/shipping" element={
        <ProtectedRoute requiredRole="shipping">
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<ScreenGate screen="ShippingDashboard"><ShippingDashboard /></ScreenGate>} />
      </Route>

      {/* Buchhaltung Routes - Protected */}
      <Route path="/buchhaltung" element={
        <ProtectedRoute requiredRole="buchhaltung">
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="finanzen" replace />} />
        <Route path="rechnungen" element={<ScreenGate screen="BuchhaltungRechnungen"><BuchhaltungRechnungen /></ScreenGate>} />
        <Route path="rechnungen/:id" element={<ScreenGate screen="BuchhaltungRechnungen">{lazyPage(<BuchhaltungRechnungEditor />)}</ScreenGate>} />
        <Route path="mahnungen" element={<ScreenGate screen="BuchhaltungMahnungen"><BuchhaltungMahnungen /></ScreenGate>} />
        <Route path="gutschriften" element={<ScreenGate screen="BuchhaltungGutschriften"><BuchhaltungGutschriften /></ScreenGate>} />
        <Route path="finanzen" element={<ScreenGate screen="BuchhaltungDashboard"><BuchhaltungDashboard /></ScreenGate>} />
        <Route path="journal" element={<ScreenGate screen="BuchhaltungJournal"><BuchhaltungJournal /></ScreenGate>} />
        <Route path="konten" element={<ScreenGate screen="BuchhaltungKonten"><BuchhaltungKonten /></ScreenGate>} />
        <Route path="berichte" element={<ScreenGate screen="BuchhaltungBerichte"><BuchhaltungBerichte /></ScreenGate>} />
        <Route path="bank" element={<ScreenGate screen="BuchhaltungBank"><BuchhaltungBank /></ScreenGate>} />
        <Route path="bank/verbinden" element={<ScreenGate screen="BuchhaltungBank">{lazyPage(<BuchhaltungBankVerbinden />)}</ScreenGate>} />
        <Route path="ausgaben" element={<ScreenGate screen="BuchhaltungAusgaben">{lazyPage(<BuchhaltungAusgaben />)}</ScreenGate>} />
        <Route path="ausgaben/:id" element={<ScreenGate screen="BuchhaltungAusgaben">{lazyPage(<BuchhaltungAusgabeEditor />)}</ScreenGate>} />
        <Route path="wiederkehrend" element={<ScreenGate screen="BuchhaltungWiederkehrend">{lazyPage(<BuchhaltungWiederkehrend />)}</ScreenGate>} />
        <Route path="wiederkehrend/:id" element={<ScreenGate screen="BuchhaltungWiederkehrend">{lazyPage(<BuchhaltungWiederkehrendEditor />)}</ScreenGate>} />
        <Route path="anlagen" element={<ScreenGate screen="BuchhaltungAnlagen"><BuchhaltungAnlagen /></ScreenGate>} />
        <Route path="abschluss" element={<ScreenGate screen="BuchhaltungAbschluss"><BuchhaltungAbschluss /></ScreenGate>} />
        <Route path="profile" element={<ScreenGate screen="AgentProfile"><AgentProfile /></ScreenGate>} />
      </Route>

      {/* Admin Routes - Protected */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin">
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<ScreenGate screen="AdminDashboard"><AdminDashboard /></ScreenGate>} />
        <Route path="leads" element={<ScreenGate screen="AdminLeads"><AdminLeads /></ScreenGate>} />
        <Route path="tasks" element={<ScreenGate screen="AdminTasks"><AdminTasks /></ScreenGate>} />
        <Route path="orders" element={<ScreenGate screen="AdminOrders"><AdminOrders /></ScreenGate>} />
        <Route path="products" element={<ScreenGate screen="AdminProducts"><AdminProducts /></ScreenGate>} />
        <Route path="stock-movements" element={<ScreenGate screen="AdminStockMovements"><AdminStockMovements /></ScreenGate>} />
        <Route path="suppliers" element={<ScreenGate screen="AdminSuppliers"><AdminSuppliers /></ScreenGate>} />
        <Route path="agents" element={<ScreenGate screen="AdminAgents"><AdminAgents /></ScreenGate>} />
        <Route path="reports" element={<ScreenGate screen="AdminReports"><AdminReports /></ScreenGate>} />
        <Route path="inbox" element={<ScreenGate screen="AdminInbox"><AdminInbox /></ScreenGate>} />
        <Route path="lead-hunter" element={<ScreenGate screen="AdminLeadHunter"><AdminLeadHunter /></ScreenGate>} />
        <Route path="performance" element={<ScreenGate screen="AdminAgentPerformance"><AdminAgentPerformance /></ScreenGate>} />
        <Route path="pdf-settings" element={<ScreenGate screen="AdminPdfSettings"><AdminPdfSettings /></ScreenGate>} />
        <Route path="ewk-kategorien" element={<ScreenGate screen="AdminEwkKategorien"><AdminEwkKategorien /></ScreenGate>} />
        <Route path="shopify-orders" element={<ScreenGate screen="AdminShopifyOrders"><AdminShopifyOrders /></ScreenGate>} />
        <Route path="kundenportal" element={<ScreenGate screen="AdminKundenportal">{lazyPage(<AdminKundenportal />)}</ScreenGate>} />
        <Route path="integrationen" element={<ScreenGate screen="AdminIntegrationen">{lazyPage(<AdminIntegrationen />)}</ScreenGate>} />
        <Route path="einstellungen" element={<ScreenGate screen="AdminEinstellungen">{lazyPage(<AdminEinstellungen />)}</ScreenGate>} />
      </Route>

      {/* Kundenportal — eigene Rolle 'kunde' (DB-Rolle kunde_portal) */}
      <Route path="/kunde/login" element={lazyPage(<KundeLogin />)} />
      <Route path="/kunde/passwort" element={lazyPage(<KundePasswort />)} />
      <Route path="/kunde" element={
        <ProtectedRoute requiredRole="kunde" loginPath="/kunde/login">
          {lazyPage(<KundeLayout />)}
        </ProtectedRoute>
      }>
        <Route index element={lazyPage(<KundeUebersicht />)} />
        <Route path="bestellungen" element={lazyPage(<KundeBestellungen />)} />
        <Route path="bestellungen/:id" element={lazyPage(<KundeBestellung />)} />
        <Route path="bestellen" element={lazyPage(<KundeBestellen />)} />
        <Route path="warenkorb" element={lazyPage(<KundeWarenkorb />)} />
        <Route path="rechnungen" element={lazyPage(<KundeRechnungen />)} />
        <Route path="zahlung/:id" element={lazyPage(<KundeZahlung />)} />
        <Route path="konto" element={lazyPage(<KundeKonto />)} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  )
}

export default App
